import { CheckCircle2, History, ShieldAlert, ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";

import { useRunValidation } from "@/api/validation";
import type { LockReason } from "@/api/hooks";
import { useSession } from "@/auth/SessionContext";
import { hasUnclassifiedFindings } from "@/lib/validationCodes";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/ui/Card";
import { Tooltip } from "@/ui/Tooltip";
import {
  DeniedState,
  ErrorState,
  LoadingState,
  UnavailableState,
} from "@/ui/states";
import { FinalQuestioningWarning } from "@/ui/project/FinalQuestioningWarning";
import { useValidationOutcome } from "@/state/validationState";

import { CandidateGateNotice } from "./CandidateGateNotice";
import { FindingList } from "./FindingList";

/**
 * The report-level surface for authoritative validation.
 *
 * Every branch below is a *stated* state. There is no branch that renders
 * nothing, and none that renders an unknown result as though it were a clean
 * one — #7 requires unknown or unavailable validation to read as fail-closed.
 *
 * The four states worth naming, because each was a contract gap rather than a
 * design choice:
 *
 *  - **passed but unbound.** `ValidationReport.draftVersion` is nullable until
 *    #15, so a report may not say which draft version it validated. That is not
 *    a pass; it is a pass that cannot be tied to anything.
 *  - **stale.** A 409 means the draft moved under the clinician and the report
 *    they were reading is void. The recovery is "validate again", not "retry".
 *  - **not available.** A 501 is terminal until the endpoint deploys. No retry
 *    is offered, because none can succeed.
 *  - **unclassifiable.** A finding set this editor cannot classify is called
 *    out explicitly rather than presented as a complete picture.
 */
export function ValidationPanel({
  projectId,
  lockReasonByPath,
  title = "Authoritative validation",
  description,
}: {
  projectId: string;
  lockReasonByPath?: ReadonlyMap<string, LockReason> | undefined;
  title?: string;
  description?: ReactNode;
}) {
  const outcome = useValidationOutcome(projectId);
  const { can } = useSession();
  const { run, isPending } = useRunValidation(projectId);

  const canRun = can("validation.run");
  const isTerminal =
    outcome.kind === "unavailable" || outcome.kind === "denied";
  const runReason = canRun
    ? null
    : 'Your account does not have the "validation.run" permission.';

  const runButton = (
    <Button
      variant="outline"
      size="sm"
      disabled={!canRun || isPending}
      onClick={run}
    >
      <ShieldCheck aria-hidden="true" className="h-4 w-4" />
      {outcome.kind === "not_run" ? "Run validation" : "Validate again"}
    </Button>
  );

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-center justify-between gap-3">
        <CardTitle>{title}</CardTitle>
        {isTerminal ? null : runReason ? (
          <Tooltip content={runReason}>{runButton}</Tooltip>
        ) : (
          runButton
        )}
      </CardHeader>

      <CardBody className="space-y-4">
        {description ? (
          <p className="text-sm text-muted-foreground">{description}</p>
        ) : null}

        {/*
          THE VALIDATION/FREEZE copy of §8's persistent safety warning (#853).

          Above the report rather than inside it, because it is not a finding:
          a disabled posture is a valid configuration that validates and
          freezes, and what a clinician needs here is that anything frozen from
          this draft CARRIES the choice. The finding that does gate the freeze
          is the separate one the service raises when the confirmation behind
          the choice has gone stale (`safety.final_questioning_evidence_stale`),
          which arrives in the report below with its own recovery.
        */}
        <FinalQuestioningWarning projectId={projectId} surface="validation" />

        <Body
          projectId={projectId}
          outcome={outcome}
          {...(lockReasonByPath ? { lockReasonByPath } : {})}
          onRun={run}
        />
      </CardBody>
    </Card>
  );
}

function Body({
  projectId,
  outcome,
  lockReasonByPath,
  onRun,
}: {
  projectId: string;
  outcome: ReturnType<typeof useValidationOutcome>;
  lockReasonByPath?: ReadonlyMap<string, LockReason> | undefined;
  onRun: () => void;
}) {
  switch (outcome.kind) {
    case "not_run":
      return (
        <>
          <p className="text-sm text-muted-foreground">
            This draft has not been validated in this session. Nothing is
            assumed about it — a draft is only ever treated as clean after the
            Editor Service says so.
          </p>
          <CandidateGateNotice projectId={projectId} />
        </>
      );

    case "running":
      return <LoadingState label="Running authoritative validation" rows={2} />;

    case "resyncing":
      // A freeze refusal disproved this session's belief and the
      // authoritative state is being re-read. Stated as the in-progress check
      // it is; the gate notice below carries the blocked verdict.
      return (
        <>
          <LoadingState
            label="Re-checking the configuration's validation state"
            rows={2}
          />
          <CandidateGateNotice projectId={projectId} />
        </>
      );

    case "unavailable":
      return (
        <>
          {/*
            501 is not an error and offers no retry: the endpoint exists in the
            contract but the Editor Service has not built it. Retrying can never
            succeed, so offering to would be a control that cannot work.
          */}
          <UnavailableState
            title="Authoritative validation is not available yet"
            description="The Editor Service accepts this request but has not implemented it. Until it does, no draft can be certified as valid here — so nothing downstream of validation is offered either."
            blockedBy="Blocked by issue #16 — authoritative validation and candidate freeze."
          />
          <CandidateGateNotice projectId={projectId} />
        </>
      );

    case "denied":
      return (
        <DeniedState
          title="You cannot run validation on this project"
          description="Validation runs against the authoritative validator on the server. Ask a workspace administrator for the validation.run permission."
        />
      );

    case "stale":
      return (
        <>
          <div
            role="alert"
            className="flex flex-col gap-3 rounded-md border border-warning-foreground/30 bg-warning/40 px-4 py-3"
          >
            <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <History aria-hidden="true" className="h-4 w-4" />
              The draft changed since this report was produced
            </p>
            <p className="text-sm text-muted-foreground">
              Validation was pinned to the draft version under review, and the
              service reported that version is no longer current. The previous
              report described a draft that no longer exists, so it has been
              discarded rather than shown.
              {outcome.requestId ? (
                <>
                  {" "}
                  Request <code className="font-mono">{outcome.requestId}</code>
                  .
                </>
              ) : null}
            </p>
            <div>
              <Button variant="outline" size="sm" onClick={onRun}>
                <ShieldCheck aria-hidden="true" className="h-4 w-4" />
                Validate the current draft
              </Button>
            </div>
          </div>
          <CandidateGateNotice projectId={projectId} />
        </>
      );

    case "error":
      return (
        <>
          {/*
            #7: "no raw stack traces or unclassified 500 messages reach the
            clinician". The server's message is never rendered — only the
            machine-readable code and the request id, which is what support
            actually needs to find the trace.
          */}
          <ErrorState
            title="Validation could not be completed"
            description={
              <>
                <p>
                  The Editor Service did not return a validation result. Nothing
                  about this draft has been assumed in either direction.
                </p>
                <p className="mt-2 font-mono text-xs">
                  {outcome.code}
                  {outcome.requestId ? ` · request ${outcome.requestId}` : ""}
                </p>
              </>
            }
            onRetry={onRun}
          />
          <CandidateGateNotice projectId={projectId} />
        </>
      );

    case "report": {
      const { report } = outcome;
      // The report is draft-scoped, not section-scoped: `runValidation` takes a
      // project and a draft pin, and returns findings for the whole draft. So
      // every finding is rendered on every section surface, and the section a
      // finding belongs to is expressed by whether its jump affordance resolves
      // here. Filtering to the current section would hide findings that name a
      // section the clinician has not opened.
      const passed = report.state === "passed";
      const bound = Boolean(report.draftVersion);
      const unclassifiable = hasUnclassifiedFindings(report.findings);

      return (
        <>
          <div
            // A completed run is announced. `alert` for a failed report because
            // it blocks the workflow; `status` for a pass so it does not
            // interrupt.
            role={passed ? "status" : "alert"}
            aria-live={passed ? "polite" : "assertive"}
            className="flex flex-wrap items-center gap-3"
          >
            {passed ? (
              <Badge tone="success">
                <CheckCircle2 aria-hidden="true" className="mr-1 h-3 w-3" />
                Passed
              </Badge>
            ) : (
              <Badge tone="destructive">
                <ShieldAlert aria-hidden="true" className="mr-1 h-3 w-3" />
                Failed
              </Badge>
            )}

            {/*
              THE UNBOUND CASE STAYS ON THE HEADLINE (#909). A report the
              service did not tie to a draft version cannot certify anything,
              and that is a fact about the RESULT a clinical author is reading,
              not evidence behind it. A bound report's version is the opposite:
              an identifier support needs and an author does not, so it moved
              into the disclosure below.
            */}
            {bound ? null : (
              <Badge tone="warning">Draft version not reported</Badge>
            )}
          </div>

          {report.findings.length > 0 ? (
            <FindingList
              findings={report.findings}
              {...(lockReasonByPath ? { lockReasonByPath } : {})}
              announce={false}
              projectId={projectId}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              The validator reported no findings.
            </p>
          )}

          {/*
            THE REPORT'S OWN EVIDENCE, COLLAPSED (#909). Which exact draft the
            verdict is bound to, why an unbound one certifies nothing, and
            whether any finding carried a code this editor cannot classify:
            each is real, each is kept, and none of them is what a clinical
            author reads a failed validation for. What stays in front of them
            is the verdict and the findings, which is what the ticket means by
            a concise status beside actionable findings.
          */}
          <details className="rounded-md border border-border">
            <summary
              aria-label="Show validation report details"
              className="cursor-pointer px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground"
            >
              Details
            </summary>
            <div className="space-y-2 border-t border-border px-4 py-3">
              {/*
                ONLY WHERE THERE IS ONE. The unbound case says so on the
                headline badge above and explains itself below; repeating
                "Draft version not reported" here would be one fact stated
                twice on one panel.
              */}
              {bound ? (
                <p className="font-mono text-xs text-muted-foreground">
                  Draft version {report.draftVersion}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  The service did not say which draft version it validated, so
                  this result cannot be tied to the draft that would be frozen.
                  It is shown as a result, not as a certification.{" "}
                  <span className="font-mono text-xs">
                    Blocked by issue #15 — draft persistence and versioning.
                  </span>
                </p>
              )}
              {unclassifiable ? (
                <p className="text-sm text-muted-foreground">
                  Some findings carry codes this editor does not recognise. They
                  are listed above with their raw codes rather than sorted into
                  a category, and their presence alone is enough to withhold
                  certification.
                </p>
              ) : null}
            </div>
          </details>

          <CandidateGateNotice projectId={projectId} />
        </>
      );
    }
  }
}
